import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
export interface GrabShiftPayload {
    requestShiftId: string;
    grabById: string;
    grabByName: string;
    requestSite: string;
    eventAction?: string[];
}

export type GrabShiftReasonCode =
    | 'DUPLICATE_CLAIM_BY_USER'
    | 'SHIFT_ALREADY_FILLED'
    | 'SHIFT_EXPIRED_OR_CANCELED'
    | 'USER_NOT_ELIGIBLE'
    | 'SHIFT_NOT_FOUND'
    | 'CONFLICT_UNKNOWN';

interface GrabShiftErrorPayload {
    message: string;
    isDuplicate: boolean;
    reasonCode: GrabShiftReasonCode | null;
}

interface GrabShiftState {
    lastGrabbed: GrabShiftPayload | null;
    loading: boolean;
    success: boolean;
    successMessage: string | null;
    isDuplicate: boolean;
    reasonCode: GrabShiftReasonCode | null;
    error: string | null;
}

const initialState: GrabShiftState = {
    lastGrabbed: null,
    loading: false,
    success: false,
    successMessage: null,
    isDuplicate: false,
    reasonCode: null,
    error: null,
};

const isGrabShiftReasonCode = (value: unknown): value is GrabShiftReasonCode => {
    return value === 'DUPLICATE_CLAIM_BY_USER'
        || value === 'SHIFT_ALREADY_FILLED'
        || value === 'SHIFT_EXPIRED_OR_CANCELED'
        || value === 'USER_NOT_ELIGIBLE'
        || value === 'SHIFT_NOT_FOUND'
        || value === 'CONFLICT_UNKNOWN';
};

const deriveReasonCode = (error: any): GrabShiftReasonCode | null => {
    const fromField = error?.reasonCode;
    if (isGrabShiftReasonCode(fromField)) {
        return fromField;
    }

    const message = String(error?.message ?? '').toLowerCase();
    if (message.includes('already claimed')) {
        return 'DUPLICATE_CLAIM_BY_USER';
    }
    if (message.includes('no longer available') || message.includes('already filled')) {
        return 'SHIFT_ALREADY_FILLED';
    }
    if (message.includes('expired') || message.includes('canceled')) {
        return 'SHIFT_EXPIRED_OR_CANCELED';
    }
    if (message.includes('not eligible')) {
        return 'USER_NOT_ELIGIBLE';
    }
    if (message.includes('not found')) {
        return 'SHIFT_NOT_FOUND';
    }

    return null;
};

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Grab/Claim a shift
 * Uses: POST /api/shift/grabShift
 * 201 - Succeeded: shift claimed successfully
 * 409 - Conflict: user already claimed this shift
 * 500 - Failed: shift no longer available or server error
 */
export const grabShift = createAsyncThunk(
    'grabShift/grabShift',
    async ({ payload }: { payload: GrabShiftPayload }, { rejectWithValue }) => {
        try {
            const response = await API_POST(`${ENDPOINTS.GRAB_SHIFT}`, payload);
            console.log('✅ Grab Shift API Response:', response);
            return response;
        } catch (error: any) {
            console.error('❌ Grab Shift API Error:', error);
            const reasonCode = deriveReasonCode(error);
            const isDuplicate = error?.statusCode === 409;
            const message = error?.message || 'Failed to grab shift';
            return rejectWithValue({ message, isDuplicate, reasonCode } as GrabShiftErrorPayload);
        }
    }
);

// ========================================
// SLICE
// ========================================
const grabShiftSlice = createSlice({
    name: 'grabShift',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.isDuplicate = false;
            state.reasonCode = null;
        },
        resetGrabShift: (state) => {
            state.lastGrabbed = null;
            state.loading = false;
            state.success = false;
            state.successMessage = null;
            state.isDuplicate = false;
            state.reasonCode = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(grabShift.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.successMessage = null;
                state.isDuplicate = false;
                state.reasonCode = null;
                state.error = null;
            })
            .addCase(grabShift.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.successMessage = action.payload?.message || null;
                state.lastGrabbed = action.meta.arg.payload;
            })
            .addCase(grabShift.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                const payload = action.payload as GrabShiftErrorPayload;
                state.isDuplicate = payload.isDuplicate;
                state.reasonCode = payload.reasonCode;
                state.error = payload.message;
            });
    },
});

export const { clearError, resetGrabShift } = grabShiftSlice.actions;
export default grabShiftSlice.reducer;